import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { appImages } from "../assets/appicons";
import { AppIcon } from "./mobileUi";

// Màu cảnh báo lấy từ mobile-app-v2 (themes/colors/default.ts)
const WARNING = "#F2C94C";
const WARNING_BANNER = "#FFECB1";
const WARNING_CONTENT = "#2F3032";

// Thời lượng animation khớp PromotionBar.tsx của app
const SHOW_MS = 320;
const HIDE_MS = 220;

/**
 * Thanh nhắc nhở chấm công (khớp mobile-app-v2: components/PromotionBar.tsx)
 * - Trượt xuống + fade in khi hiện, fade out khi ẩn
 * - Tự ẩn sau `autoHideMs` (0 = tắt tự ẩn)
 * - Bấm vào thanh -> onClick, bấm dấu X -> ẩn
 */
const PromotionBar = ({
  message,
  topInset = 8,
  autoHideMs = 6000,
  onClick,
  onClose,
}) => {
  const [mounted, setMounted] = useState(true);
  const [shown, setShown] = useState(false);

  const anchorRef = useRef(null);
  const autoHideTimerRef = useRef(null);
  const unmountTimerRef = useRef(null);

  // Đo cột nội dung để thanh nổi đúng bề ngang màn (left: 16, right: 16 như app)
  const [anchor, setAnchor] = useState(null);

  useEffect(() => {
    const measure = () => {
      const el = anchorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setAnchor({ left: rect.left, width: rect.width });
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const hide = useCallback(() => {
    if (autoHideTimerRef.current) clearTimeout(autoHideTimerRef.current);
    setShown(false);
    unmountTimerRef.current = setTimeout(() => {
      setMounted(false);
      if (onClose) onClose();
    }, HIDE_MS);
  }, [onClose]);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setShown(true));

    if (autoHideMs > 0) {
      autoHideTimerRef.current = setTimeout(hide, autoHideMs);
    }

    return () => {
      cancelAnimationFrame(raf);
      if (autoHideTimerRef.current) clearTimeout(autoHideTimerRef.current);
      if (unmountTimerRef.current) clearTimeout(unmountTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!mounted) return <Box ref={anchorRef} h="0" />;

  return (
    <Box ref={anchorRef} h="0">
      {anchor && (
        <Box
          position="fixed"
          top={`${topInset}px`}
          left={`${anchor.left + 16}px`}
          w={`${Math.max(anchor.width - 32, 0)}px`}
          zIndex={50}
          opacity={shown ? 1 : 0}
          transform={shown ? "translateY(0)" : "translateY(-24px)"}
          transition={
            shown
              ? `opacity ${SHOW_MS}ms cubic-bezier(0.215,0.61,0.355,1), transform ${SHOW_MS}ms cubic-bezier(0.215,0.61,0.355,1)`
              : `opacity ${HIDE_MS}ms cubic-bezier(0.55,0.055,0.675,0.19), transform ${HIDE_MS}ms cubic-bezier(0.55,0.055,0.675,0.19)`
          }
          pointerEvents="none"
        >
          <Flex
            as={onClick ? "button" : "div"}
            type={onClick ? "button" : undefined}
            onClick={onClick}
            cursor={onClick ? "pointer" : "default"}
            position="relative"
            w="100%"
            align="center"
            gap="12px"
            border="1px solid"
            borderColor={WARNING}
            borderRadius="14px"
            bg={WARNING_BANNER}
            px="12px"
            py="12px"
            textAlign="left"
            boxShadow="0 6px 12px rgba(0,0,0,0.18)"
            pointerEvents="auto"
          >
            <Flex
              w="36px"
              h="36px"
              borderRadius="10px"
              bg={WARNING}
              align="center"
              justify="center"
              flexShrink={0}
            >
              <AppIcon
                src={appImages.icon_triangle_alert}
                size={20}
                color={WARNING_CONTENT}
              />
            </Flex>

            <Box flex="1" pr="18px" minW="0">
              <Text fontSize="14px" fontWeight={600} color={WARNING_CONTENT}>
                Nhắc nhở chấm công
              </Text>
              <Text
                fontSize="14px"
                fontWeight={400}
                color={WARNING_CONTENT}
                mt="2px"
                whiteSpace="normal"
              >
                {message}
              </Text>
            </Box>

            <Flex
              as="span"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                hide();
              }}
              position="absolute"
              top="8px"
              right="8px"
              w="22px"
              h="22px"
              borderRadius="11px"
              align="center"
              justify="center"
              cursor="pointer"
            >
              <AppIcon
                src={appImages.icon_close}
                size={11}
                color={WARNING_CONTENT}
              />
            </Flex>
          </Flex>
        </Box>
      )}
    </Box>
  );
};

export default PromotionBar;
